import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, ArrowRight } from "lucide-react";
import { ADDRESS, HOURS, PHONE_DISPLAY, PHONE_TEL } from "@/data";

export default function OrderSection() {
  return (
    <section id="order" className="border-t border-zinc-800 bg-coal-800" data-testid="order-section">
      <div className="max-w-7xl mx-auto px-6 py-28 lg:py-40 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
        >
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-ember mb-4">Order Online · Pay Online · Pickup In Store</p>
          <h2 className="font-display text-6xl sm:text-7xl leading-none uppercase mb-8 text-gold-shine" data-testid="order-heading">
            Get it<br />fired up
          </h2>
          <p className="text-zinc-400 text-base leading-relaxed max-w-md mb-10">
            Browse the full menu, customise with extras, choose your pickup time and pay securely by card — then just walk in and grab it.
          </p>
          <Link
            to="/order"
            data-testid="order-start-button"
            className="inline-flex items-center gap-3 bg-ember hover:bg-ember-hover text-white font-semibold px-10 py-5 text-sm tracking-wide uppercase transition-colors duration-300 ember-glow"
          >
            Start Your Order <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="mt-6 font-mono text-xs text-zinc-500">100+ items · sizes & extras · secure card payment</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="space-y-12"
        >
          <div>
            <h3 className="font-mono text-xs tracking-[0.3em] uppercase text-ember mb-6">Call to order</h3>
            <a
              href={PHONE_TEL}
              data-testid="call-to-order-button"
              className="group flex items-center gap-5 border border-zinc-800 hover:border-ember p-6 transition-colors duration-300"
            >
              <span className="w-14 h-14 flex items-center justify-center bg-ember text-white group-hover:scale-110 transition-transform duration-300">
                <Phone className="w-6 h-6" />
              </span>
              <span>
                <span className="block font-display text-4xl tracking-wide">{PHONE_DISPLAY}</span>
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">Fastest way after midnight</span>
              </span>
            </a>
          </div>

          <div>
            <h3 className="font-mono text-xs tracking-[0.3em] uppercase text-ember mb-6 flex items-center gap-2">
              <Clock className="w-4 h-4" /> Hours
            </h3>
            <ul className="space-y-3" data-testid="hours-list">
              {HOURS.map((h) => (
                <li key={h.days} className="flex items-baseline gap-4 text-sm">
                  <span className="text-zinc-300 w-28">{h.days}</span>
                  <span className="flex-1 border-b border-dotted border-zinc-800" aria-hidden="true" />
                  <span className="font-mono text-ember">{h.time}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-mono text-xs tracking-[0.3em] uppercase text-ember mb-6 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Find us
            </h3>
            <p className="text-zinc-300 text-lg mb-4" data-testid="contact-address">{ADDRESS}</p>
            <a
              href="https://share.google/bCo3OBj6DvDCvfAfj"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="directions-link"
              className="font-mono text-xs tracking-[0.2em] uppercase text-ember hover:text-ink transition-colors duration-300"
            >
              Get directions →
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
